// src/pages/PharmaReports.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import _ from "lodash";
import { toast } from "react-toastify";

import AppConst from "../apis/appconst";
import DelBox from "../comps/DelBox";
import NewOrder from "../models/NewOrder";

const pad = (n) => String(n).padStart(2, "0");
const getTodayYMD = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

function parseServerDateToLocal(s) {
  if (!s) return null;
  if (s instanceof Date) return s;
  const normalized = String(s).trim().replace(" ", "T");
  const m = normalized.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/
  );
  if (!m) {
    const fallback = new Date(s);
    return isNaN(fallback) ? null : fallback;
  }
  const year = Number(m[1]),
    month = Number(m[2]) - 1,
    day = Number(m[3]);
  const hour = Number(m[4] ?? 0),
    minute = Number(m[5] ?? 0),
    second = Number(m[6] ?? 0);
  return new Date(year, month, day, hour, minute, second);
}

const formatDateTime12 = (s) => {
  const dt = parseServerDateToLocal(s);
  if (!dt) return "";
  const day = pad(dt.getDate());
  const months = [
    "Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"
  ];
  const mon = months[dt.getMonth()];
  let hr = dt.getHours();
  const min = pad(dt.getMinutes());
  const ampm = hr >= 12 ? "PM" : "AM";
  hr = hr % 12 || 12;
  return `${day}-${mon}-${dt.getFullYear()} ${pad(hr)}:${min} ${ampm}`;
};

function classifyMode(raw = "") {
  const s = String(raw || "").toLowerCase();
  if (s.includes("cash")) return "Cash";
  if (s.includes("card") || s.includes("debit") || s.includes("credit") || s.includes("swipe")) return "Card";
  if (s.includes("upi") || s.includes("online") || s.includes("gateway") || s.includes("net")) return "Online";
  return raw || "";
}

export default function PharmaReports() {
  const today = getTodayYMD();

  const [masterList, setMasterList] = useState([]);
  const [doctorsList, setDoctorsList] = useState([]);
  const [users, setUsers] = useState([]); // derived users for filter
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    search_key: "",
    from_date: today,
    to_date: today,
    selectedDoctor: "",
    selectedUser: "",
    paymentType: "All", // new payment filter
    page_no: "none"
  });

  const [summary, setSummary] = useState({
    totalSaleAmount: 0,
    totalPatients: 0,
    todaysCollection: 0,
    collectionBy: { cash: 0, online: 0, card: 0 }
  });

  const [totals, setTotals] = useState({
    totalAmount: 0,
    discountAmount: 0,
    paidAmount: 0,
    dueAmount: 0,
    totalRows: 0,
    doctorCommission: 0,
    doctorCommissionPaid: 0,
    doctorCommissionDue: 0
  });

  // ---------- API calls ----------
  const initData = useCallback(async (currentForm = formData) => {
    try {
      setLoading(true);
      const stored = JSON.parse(sessionStorage.getItem("ldata") || "{}");
      // if (!stored.user_id) { toast.warn("User not logged in."); setLoading(false); return; }
      const fd = new FormData();
      fd.append("page_no", currentForm.page_no ?? "none");
      fd.append("search_key", currentForm.search_key ?? "");
      fd.append("from_date", currentForm.from_date ?? "");
      fd.append("to_date", currentForm.to_date ?? "");
      // fd.append("med_bill_user", stored.user_id); // uncomment if you want to restrict by logged user
      fd.append("med_bill_type", "all");
      if (currentForm.selectedDoctor) fd.append("doctor_id", currentForm.selectedDoctor);

      const res = await axios.post(AppConst.BASE_URL + AppConst.getMedBillListUrl, fd);
      if (res?.data?.status && Array.isArray(res.data.records)) {
        setMasterList(res.data.records);
      } else {
        setMasterList([]);
      }
    } catch (err) {
      console.error("initData error", err);
      setMasterList([]);
    } finally {
      setLoading(false);
    }
  }, [formData]);

  const fetchDoctors = async () => {
    try {
      const stored = JSON.parse(sessionStorage.getItem("ldata") || "{}");
      const fd = new FormData();
      fd.append("type", "doctor");
      fd.append("user_cre_by", stored.user_id ?? "");
      const res = await axios.post(AppConst.BASE_URL + AppConst.getUserListUrl, fd);
      if (res?.data?.status && Array.isArray(res.data.records)) {
        setDoctorsList(res.data.records);
      }
    } catch (e) {
      console.error("fetchDoctors error", e);
    }
  };

  // ---------- Life cycle ----------
  useEffect(() => {
    initData(formData);
    fetchDoctors();
    // eslint-disable-next-line
  }, []);

  // derive users from masterList for user filter dropdown
  useEffect(() => {
    const map = {};
    masterList.forEach(r => {
      const uid = r.med_bill_user ?? r.user_id ?? r.created_by ?? null;
      const uname = r.user_display || r.last_used || r.fname || `User#${uid}`; 
      if (uid) map[String(uid)] = uname;
    });
    setUsers(Object.entries(map).map(([user_id, name]) => ({ user_id, name })));
  }, [masterList]);

  // ---------- Filtering for DataTable ----------
  const filteredRecords = useMemo(() => {
    let data = [...masterList];

    // Filter by med_bill_datetime range (use med_bill_datetime if available, fallback to med_bill_date)
    if (formData.from_date) {
      const from = new Date(`${formData.from_date}T00:00`);
      data = data.filter(r => {
        const raw = r.med_bill_datetime ?? r.med_bill_date ?? null;
        const d = parseServerDateToLocal(raw);
        return d ? d >= from : true;
      });
    }
    if (formData.to_date) {
      const to = new Date(`${formData.to_date}T23:59:59`);
      data = data.filter(r => {
        const raw = r.med_bill_datetime ?? r.med_bill_date ?? null;
        const d = parseServerDateToLocal(raw);
        return d ? d <= to : true;
      });
    }

    // Doctor filter
    if (formData.selectedDoctor) {
      const sel = String(formData.selectedDoctor).toLowerCase();
      data = data.filter(r => {
        const idMatch = String(r.med_bill_ref_doctor ?? r.med_bill_doctor ?? "").toLowerCase() === sel;
        const nameMatch = ((r.med_bill_ref_doctor_name ?? r.med_bill_doctor_name ?? "") + "").toLowerCase().includes(sel);
        return idMatch || nameMatch;
      });
    }

    // User filter
    if (formData.selectedUser) {
      data = data.filter(r => String(r.med_bill_user || "").toLowerCase() === String(formData.selectedUser).toLowerCase());
    }

    // Payment filter (All/Cash/Card/Online)
    if (formData.paymentType && formData.paymentType !== "All") {
      const wanted = formData.paymentType.toLowerCase();
      data = data.filter(r => {
        const modeRaw = (r.med_bill_payment_mode || r.med_bill_paymentmode || r.med_bill_mode_of_payment || r.med_bill_payment_mode || "");
        const mode = classifyMode(modeRaw).toLowerCase();
        return mode === wanted;
      });
    }

    // Search key client-side
    if (formData.search_key && formData.search_key.trim() !== "") {
      const sk = formData.search_key.trim().toLowerCase();
      data = data.filter(r => {
        const uhid = (r.med_bill_ipd_number || "").toString().toLowerCase();
        const name = (r.med_bill_patient_number || "").toString().toLowerCase();
        const phone = (r.med_bill_phone_number || r.med_bill_patient_mobile || "").toString().toLowerCase();
        return uhid.includes(sk) || name.includes(sk) || phone.includes(sk);
      });
    }

    return data;
  }, [masterList, formData]);

  // ---------- Totals derived from filteredRecords ----------
  useEffect(() => {
    if (!filteredRecords || filteredRecords.length === 0) {
      setSummary({ totalSaleAmount: 0, totalPatients: 0, todaysCollection: 0, collectionBy: { cash: 0, online: 0, card: 0 }});
      setTotals({ totalAmount: 0, discountAmount: 0, paidAmount: 0, dueAmount: 0, totalRows: 0, doctorCommission: 0, doctorCommissionPaid: 0, doctorCommissionDue: 0 });
      return;
    }
    let totalSale = 0, totalDisc = 0, totalPaid = 0, totalDue = 0, totalComm = 0;
    let cash = 0, card = 0, online = 0, todaysColl = 0;
    const uniquePatients = new Set(filteredRecords.map(i => i.med_bill_ipd_number || i.med_bill_patient_number));

    filteredRecords.forEach(item => {
      const finalAmt = parseFloat(item.med_bill_final_amt) || 0;
      const discount = parseFloat(item.med_bill_discount_amt || item.med_bill_discount || item.med_bill_disc) || 0;
      const paid = parseFloat(item.med_bill_paid) || 0;
      const due = parseFloat(item.med_bill_due) || 0;
      const commission = parseFloat(item.doctor_commission || item.med_bill_doctor_commission) || 0;

      totalSale += finalAmt;
      totalDisc += discount;
      totalPaid += paid;
      totalDue += due;
      totalComm += commission;

      const mode = classifyMode(item.med_bill_payment_mode || item.med_bill_paymentmode || item.med_bill_mode_of_payment || "");
      if (mode === "Cash") cash += paid;
      else if (mode === "Card") card += paid;
      else if (mode === "Online") online += paid;
      else cash += paid;

      // today's collection based on med_bill_datetime (or med_bill_date fallback)
      const raw = item.med_bill_datetime ?? item.med_bill_date ?? "";
      const billDateStr = String(raw).split("T")[0];
      if (billDateStr === today) todaysColl += paid;
    });

    setSummary({
      totalSaleAmount: totalSale,
      totalPatients: uniquePatients.size,
      todaysCollection: todaysColl,
      collectionBy: { cash, online, card }
    });

    setTotals({
      totalAmount: totalSale,
      discountAmount: totalDisc,
      paidAmount: totalPaid,
      dueAmount: totalDue,
      totalRows: filteredRecords.length,
      doctorCommission: totalComm,
      doctorCommissionPaid: 0,
      doctorCommissionDue: 0
    });
  }, [filteredRecords, today]);

  // ---------- Handlers ----------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // immediate backend fetch for date changes
    if (name === "from_date" || name === "to_date") {
      setTimeout(() => initData({ ...formData, [name]: value }), 50);
    }
  };

  const debouncedInit = useCallback(_.debounce(initData, 500), [initData]);
  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    debouncedInit({ ...formData, [name]: value });
  };

  const handleDoctorChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    initData({ ...formData, [name]: value });
  };

  const handleUserChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const viewMedicines = (row) => {
    const items = Array.isArray(row.items) ? row.items : [];
    if (items.length === 0) {
      Swal.fire({ icon: "info", title: "No items", text: "No medicines found for this bill." });
      return;
    }

    const html = `
      <div style="max-height:400px; overflow:auto; text-align:left;">
        <table class="table table-bordered" style="width:100%; font-size:13px;">
          <thead><tr>
            <th>#</th><th>Medicine</th><th>Batch</th><th>Strips</th><th>Loose</th><th>EXP</th><th>MRP</th>
          </tr></thead>
          <tbody>
            ${items.map((m, i) => `
              <tr>
                <td>${i+1}</td>
                <td>${m.med_bill_item_medicine || ""}</td>
                <td>${m.med_bill_item_batch || ""}</td>
                <td>${m.med_bill_item_strips || ""}</td>
                <td>${m.med_bill_item_is_loose ? "Yes" : "No"}</td>
                <td>${m.med_bill_item_exp || ""}</td>
                <td>${m.med_bill_item_mrp || ""}</td>
              </tr>
            `).join("")}
          </tbody>
        </table>
      </div>
    `;

    Swal.fire({
      title: `Medicines — Bill ${row.med_bill_ipd_number || row.med_bill_id || ""}`,
      html,
      width: 900,
      showCloseButton: true,
      confirmButtonText: "Close"
    });
  };

  const handlePrint = (row) => {
    Swal.fire({ icon: "info", text: "Print handler called (hook your PDF function here)." });
  };

  // ---------- DataTable columns ----------
  const columns = [
    { name: "SL", selector: (r,i) => i+1, width: "60px", center: true },
    { name: "UHID", selector: r => r.med_bill_ipd_number || "", sortable: true, width: "140px" },
    { name: "Patient", selector: r => r.med_bill_patient_number || "", sortable: true, wrap: true },
    { name: "Referral", selector: r => r.med_bill_ref_doctor_name || r.med_bill_ref_doctor || "", sortable: true, width: "180px" },
    { name: "Payment Type", selector: r => classifyMode(r.med_bill_payment_mode || r.med_bill_paymentmode || r.med_bill_mode_of_payment || ""), sortable: true, width: "140px" },
    { name: "Total", selector: r => Number(r.med_bill_final_amt || 0).toFixed(2), sortable: true, right: true, width: "110px" },
    { name: "Paid", selector: r => Number(r.med_bill_paid || 0).toFixed(2), right: true, width: "110px" },
    { name: "Due", selector: r => Number(r.med_bill_due || 0).toFixed(2), right: true, width: "110px" },
    { name: "Date", selector: r => r.med_bill_datetime || r.med_bill_date || "", cell: r => formatDateTime12(r.med_bill_datetime || r.med_bill_date), sortable: true, width: "180px" },
    { name: "User", selector: r => r.user_display || r.med_bill_user || "", width: "160px" },
    {
      name: "Actions",
      width: "180px",
      cell: (row) => (
        <div className="actions-cell">
          <button title="View medicines" onClick={() => viewMedicines(row)} className="btn btn-sm btn-outline-primary view-btn me-2">
            <i className="ri-eye-line"></i> View
          </button>
          <button onClick={() => handlePrint(row)} className="btn btn-sm btn-outline-info">
            <i className="ri-print-fill"></i> Print
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true
    }
  ];

  const customStyles = {
    headCells: { style: { backgroundColor: "#f1f5fb", color: "#405189", fontSize: "14px", fontWeight: 700 } },
    rows: { style: { minHeight: "56px" } },
    cells: { style: { paddingLeft: "12px", paddingRight: "12px" } }
  };

  const HoverStyle = () => (
    <style>{`
      .rdt_TableRow .view-btn { visibility: hidden; opacity: 0; transform: translateY(-4px); transition: all .15s ease; }
      .rdt_TableRow:hover .view-btn { visibility: visible; opacity: 1; transform: translateY(0); }
      .actions-cell { display:flex; align-items:center; gap:6px; }
    `}</style>
  );

  return (
    <div className="page-content">
      <div className="container-fluid">
        <HoverStyle />

        {/* summary cards */}
        <div className="row mb-3">
          <div className="col-md-3"><div className="card p-3"><p className="mb-1 text-muted">Total Patients</p><h4>{summary.totalPatients}</h4></div></div>
          <div className="col-md-3"><div className="card p-3"><p className="mb-1 text-muted">Total Amount</p><h4>₹{totals.totalAmount}</h4></div></div>
          <div className="col-md-3"><div className="card p-3"><p className="mb-1 text-muted">Paid</p><h4>₹{totals.paidAmount}</h4></div></div>
          <div className="col-md-3"><div className="card p-3"><p className="mb-1 text-muted">Total Due</p><h4>₹{totals.dueAmount}</h4></div></div>
        </div>

        {/* filter panel */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2">
              <div className="col-md-3">
                <label>Search</label>
                <input name="search_key" value={formData.search_key} onChange={handleSearchChange} className="form-control" placeholder="UHID / Patient / Mobile"/>
              </div>

              <div className="col-md-2"><label>From</label><input type="date" name="from_date" value={formData.from_date} onChange={handleChange} className="form-control" /></div>
              <div className="col-md-2"><label>To</label><input type="date" name="to_date" value={formData.to_date} onChange={handleChange} className="form-control" /></div>

              <div className="col-md-2">
                <label>Doctor</label>
                <select name="selectedDoctor" value={formData.selectedDoctor} onChange={handleDoctorChange} className="form-select">
                  <option value="">All Doctors</option>
                  {doctorsList.map(d => <option key={d.user_id} value={d.user_id}>{d.fname || d.name || d.username}</option>)}
                </select>
              </div>

              <div className="col-md-3">
                <label>User</label>
                <select name="selectedUser" value={formData.selectedUser} onChange={handleUserChange} className="form-select">
                  <option value="">All Users</option>
                  {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                </select>
              </div>
            </div>

            <div className="row g-2 mt-3">
              <div className="col-md-2">
                <label>Payment</label>
                <select name="paymentType" value={formData.paymentType} onChange={handlePaymentChange} className="form-select">
                  <option value="All">All</option>
                  <option value="Cash">Cash</option>
                  <option value="Card">Card</option>
                  <option value="Online">Online</option>
                </select>
              </div>

              <div className="col-md-4 d-flex align-items-end">
                <div className="d-flex gap-2">
                  <button className="btn btn-success" onClick={() => initData(formData)}>Search</button>
                  <button className="btn btn-info" onClick={() => {
                    const cleared = { search_key: "", from_date: today, to_date: today, selectedDoctor: "", selectedUser: "", paymentType: "All", page_no: "none" };
                    setFormData(cleared);
                    initData(cleared);
                  }}>Clear</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* table */}
        <div className="card">
          <div className="card-body">
            <DataTable
              columns={columns}
              data={filteredRecords}
              customStyles={customStyles}
              pagination
              highlightOnHover
              persistTableHead
              responsive
              progressPending={loading}
              noHeader
              dense
              onRowDoubleClicked={(r) => viewMedicines(r)}
            />

            <div className="text-end mt-3 text-muted">
              <strong>Total Amount:</strong> ₹{(totals.totalAmount || 0).toFixed(2)} &nbsp;|&nbsp;
              <strong>Discount:</strong> ₹{(totals.discountAmount || 0).toFixed(2)} &nbsp;|&nbsp;
              <strong>Paid:</strong> ₹{(totals.paidAmount || 0).toFixed(2)} &nbsp;|&nbsp;
              <strong>Due:</strong> ₹{(totals.dueAmount || 0).toFixed(2)} &nbsp;|&nbsp;
              <strong>Rows:</strong> {totals.totalRows}
            </div>
          </div>
        </div>

        <DelBox />
        <NewOrder />
      </div>
    </div>
  );
}
